import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { type AsociacionListItem, type AsociacionRequest } from '../types/asociacion';
import { AsociacionService } from '../services';

interface AsociacionState {
  // Data state
  asociaciones: AsociacionListItem[];

  // Loading states
  loading: boolean;
  saving: boolean;

  // Error states
  error: string | null;

  // UI state
  selectedAsociacion: AsociacionListItem | null;

  // Actions
  fetchAsociaciones: () => Promise<void>;
  saveAsociacion: (asociacionRequest: AsociacionRequest) => Promise<AsociacionListItem>;
  setSelectedAsociacion: (asociacion: AsociacionListItem | null) => void;
  clearError: () => void;
}

export const useAsociacionStore = create<AsociacionState>()(
  devtools(
    (set, get) => ({
      // Initial state
      asociaciones: [],
      loading: false,
      saving: false,
      error: null,
      selectedAsociacion: null,

      // Fetch all asociaciones
      fetchAsociaciones: async () => {
        set({ loading: true, error: null });
        try {
          const asociaciones = await AsociacionService.getAsociaciones();
          set({
            asociaciones,
            loading: false
          });
        } catch (error) {
          console.error('Error in fetchAsociaciones:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch asociaciones',
            loading: false
          });
        }
      },

      // Save asociacion (create or update)
      saveAsociacion: async (asociacionRequest: AsociacionRequest): Promise<AsociacionListItem> => {
        set({ saving: true, error: null });
        try {
          const savedAsociacion = await AsociacionService.saveAsociacion(asociacionRequest);

          // Update the store based on whether it's create or update
          const currentAsociaciones = get().asociaciones;
          let updatedAsociaciones: AsociacionListItem[];

          if (asociacionRequest.idAsociacion) {
            // Update existing
            updatedAsociaciones = currentAsociaciones.map(a =>
              a.idAsociacion === asociacionRequest.idAsociacion ? savedAsociacion : a
            );
          } else {
            // Add new
            updatedAsociaciones = [savedAsociacion, ...currentAsociaciones];
          }

          set({
            asociaciones: updatedAsociaciones,
            saving: false
          });

          return savedAsociacion;
        } catch (error) {
          console.error('Error in saveAsociacion:', error);
          const errorMessage = error instanceof Error ? error.message : 'Failed to save asociacion';
          set({
            error: errorMessage,
            saving: false
          });
          throw error;
        }
      },

      // Set selected asociacion
      setSelectedAsociacion: (asociacion: AsociacionListItem | null) => {
        set({ selectedAsociacion: asociacion });
      },

      // Clear error
      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'asociacion-store',
    }
  )
);

// Selector hooks for specific data
export const useAsociaciones = () => useAsociacionStore((state) => state.asociaciones);
export const useAsociacionesLoading = () => useAsociacionStore((state) => state.loading);
export const useAsociacionesError = () => useAsociacionStore((state) => state.error);

export const useFetchAsociaciones = () => useAsociacionStore((state) => state.fetchAsociaciones);
export const useSaveAsociacion = () => useAsociacionStore((state) => state.saveAsociacion);
export const useSetSelectedAsociacion = () => useAsociacionStore((state) => state.setSelectedAsociacion);
export const useClearAsociacionError = () => useAsociacionStore((state) => state.clearError);

export const useSelectedAsociacion = () => useAsociacionStore((state) => state.selectedAsociacion);
export const useAsociacionSaving = () => useAsociacionStore((state) => state.saving);